(function (root, factory) {
  const api = typeof module === 'object' ? factory(require('./vendor/hull.js'), require('./vendor/clipper.js'),
    require('./vendor/decomp.min.js'), require('./vendor/SAT.min.js'), require('./vendor/earcut.min.js')) : factory(root.hull, root.ClipperLib, root.decomp, root.SAT, root.earcut);
  if (typeof module === 'object') module.exports = api;
  else root.ContourGeometry = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function (hull, Clipper, decomp, SAT, earcut) {
  'use strict';
  const SCALE = 100, ARC_ERROR = .25;
  const area = poly => Math.abs(poly.reduce((sum, a, i) => {
    const b = poly[(i + 1) % poly.length]; return sum + a[0] * b[1] - a[1] * b[0];
  }, 0) / 2);
  const corners = r => [[r.x - r.width / 2, r.y - r.height / 2], [r.x + r.width / 2, r.y - r.height / 2],
    [r.x + r.width / 2, r.y + r.height / 2], [r.x - r.width / 2, r.y + r.height / 2]];
  const translate = (poly, x, y) => poly.map(p => [p[0] + x, p[1] + y]);
  const fromPath = p => p.map(v => [v.X / SCALE, v.Y / SCALE]);
  function toPath(poly) {
    const p = poly.map(v => ({ X: Math.round(v[0] * SCALE), Y: Math.round(v[1] * SCALE) }));
    if (!Clipper.Clipper.Orientation(p)) p.reverse();
    return p;
  }
  function boolean(subjects, clips, operation) {
    const c = new Clipper.Clipper(), solution = [];
    c.AddPaths(subjects.map(toPath), Clipper.PolyType.ptSubject, true);
    if (clips.length) c.AddPaths(clips.map(toPath), Clipper.PolyType.ptClip, true);
    c.Execute(operation, solution, Clipper.PolyFillType.pftNonZero, Clipper.PolyFillType.pftNonZero);
    return solution.map(fromPath);
  }
  const intersection = (a, b) => boolean([a], [b], Clipper.ClipType.ctIntersection);
  const difference = (a, b) => boolean([a], [b], Clipper.ClipType.ctDifference);
  function offset(poly, distance) {
    return offsetAll([poly], distance);
  }
  function offsetAll(polygons, distance) {
    const c = new Clipper.ClipperOffset(2, ARC_ERROR * SCALE), solution = [];
    c.AddPaths(polygons.map(toPath), Clipper.JoinType.jtRound, Clipper.EndType.etClosedPolygon);
    c.Execute(solution, distance * SCALE);
    return solution.map(fromPath).sort((a, b) => area(b) - area(a))[0];
  }
  function convex(poly) { return hull(poly.map(p => [...p]), Infinity).slice(0, -1); }
  function parts(poly) {
    const vertices = poly.map(p => [...p]);
    decomp.makeCCW(vertices); decomp.removeDuplicatePoints(vertices, .0001);
    decomp.removeCollinearPoints(vertices, 0);
    let pieces = decomp.quickDecomp(vertices).filter(p => p.length >= 3 && area(p) > .0001);
    const accurate = () => Math.abs(pieces.reduce((s, p) => s + area(p), 0) - area(poly)) <= .01;
    const isConvex = p => p.every((a, i) => {
      const b = p[(i + 1) % p.length], c = p[(i + 2) % p.length];
      return (b[0] - a[0]) * (c[1] - b[1]) - (b[1] - a[1]) * (c[0] - b[0]) >= -1e-7;
    });
    // Nearly collinear notches can defeat quick decomposition. Earcut provides
    // a robust triangulation without replacing the contour by its convex hull.
    if (!accurate() || !pieces.every(isConvex)) {
      const indices = earcut(poly.flat()); pieces = [];
      for (let i = 0; i < indices.length; i += 3) {
        const triangle = indices.slice(i, i + 3).map(j => [...poly[j]]); decomp.makeCCW(triangle); pieces.push(triangle);
      }
      if (!accurate()) throw new Error('Contour triangulation lost area');
    }
    return pieces.map(p => new SAT.Polygon(new SAT.Vector(), p.map(v => new SAT.Vector(v[0], v[1]))));
  }
  function contact(partsA, a, partsB, b) {
    let best = null;
    for (const p of partsA) {
      p.pos.x = a.x; p.pos.y = a.y;
      for (const q of partsB) {
        q.pos.x = b.x; q.pos.y = b.y;
        const response = new SAT.Response();
        if (SAT.testPolygonPolygon(p, q, response) && response.overlap > .00001 && (!best || response.overlap > best.depth))
          best = { x: response.overlapV.x, y: response.overlapV.y, depth: response.overlap };
      }
    }
    return best;
  }
  function bounds(poly) {
    const xs = poly.map(p => p[0]), ys = poly.map(p => p[1]);
    const l = Math.min(...xs), r = Math.max(...xs), t = Math.min(...ys), b = Math.max(...ys);
    return { x: (l + r) / 2, y: (t + b) / 2, width: r - l, height: b - t };
  }
  function build(rects, options) {
    const top = [...rects].sort((a, b) => a.y - a.height / 2 - b.y + b.height / 2)[0];
    const title = { x: top.x, y: top.y - top.height / 2 - 23, width: 112, height: 20 };
    const members = [...rects, title], points = members.flatMap(corners);
    const concave = hull(points.map(p => [...p]), options.contourDetail).slice(0, -1);
    // Union with entire member rectangles: a concave hull through their corners
    // alone can otherwise cut through a member's interior.
    const union = boolean([concave, ...members.map(corners)], [], Clipper.ClipType.ctUnion).sort((a, b) => area(b) - area(a));
    const smoothing = options.contourSmooth;
    // Filling holes is intentional: each group has one solid bubble. Positive
    // padding also joins pieces that initially touch only at a hull vertex.
    const grown = offsetAll(union, options.outerPadding + smoothing + .6);
    const polygon = smoothing ? offset(grown, -smoothing) : grown;
    if (!polygon || polygon.length < 3) throw new Error('Empty contour');
    const r = bounds(polygon), local = translate(polygon, -r.x, -r.y);
    // The extra unit covers polygonal arc approximation and integer rounding.
    const buffer = offset(local, options.outerGap / 2 + 1);
    return { ...r, polygon: local, buffer, parts: parts(buffer), convexParts: parts(convex(buffer)),
      title: { ...title, x: title.x - r.x, y: title.y - r.y } };
  }
  return { area, corners, translate, intersection, difference, offset, convex, parts, contact, bounds, build };
});
